import { Link } from 'react-router-dom'
import { ArrowDownTrayIcon, ArrowRightIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import SubscriptionStatus from '../components/subscription/SubscriptionStatus'
import SEOHead from '../components/SEOHead'
import { LoadingSpinner } from '../components/Loading'

interface Tool {
  id: string
  name: string
  description: string
  icon: React.ReactNode
  path: string
}

const tools: Tool[] = [
  // Add more tools here as you create them
]

export default function ToolsDashboard() {
  const [flippedCards, setFlippedCards] = useState<Set<string>>(new Set())

  // Debug: Log when component renders
  console.log('ToolsDashboard rendered, tools count:', tools.length)

  const handleCardClick = (toolId: string, e: React.MouseEvent) => {
    e.preventDefault()
    setFlippedCards(prev => {
      const newSet = new Set(prev)
      if (newSet.has(toolId)) {
        newSet.delete(toolId)
      } else {
        newSet.add(toolId)
      }
      return newSet
    })

    // Navigate after animation completes
    setTimeout(() => {
      const tool = tools.find(t => t.id === toolId)
      if (tool) {
        window.location.href = tool.path
      }
    }, 300)
  }

  return (
    <>
      <SEOHead
        title="TOOL BOX"
        description="Explore custom tools and utilities from THE LOST+UNFOUNDS. Free downloads, TikTok tools, and more creative resources for builders."
        canonicalPath="/tools"
      />
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div className="text-center mb-6">
        <h1 className="text-5xl font-bold text-white mb-4 uppercase">
          Tool Box
        </h1>
        <p className="text-sm text-white/80">
          Try one of these custom tools!
        </p>
      </div>

      <div className="mb-6">
        <SubscriptionStatus />
      </div>

      <div className="space-y-3">
        {tools.map((tool) => {
          const isFlipped = flippedCards.has(tool.id)
          return (
            <div
              key={tool.id}
              className={`tool-card ${isFlipped ? 'flipped' : ''}`}
            >
              <div className="tool-card-inner">
                <Link
                  to={tool.path}
                  className="tool-card-front group bg-white/5 rounded-none px-6 py-4 hover:bg-white/10 transition-all duration-300 flex items-center gap-4 cursor-pointer w-full no-underline"
                  onClick={(e) => {
                    e.preventDefault();
                    handleCardClick(tool.id, e);
                  }}
                >
                  {/* Icon Container - Fixed width, doesn't shrink */}
                  <div className="w-10 h-10 bg-white/10 flex items-center justify-center group-hover:scale-110 transition-all flex-shrink-0">
                    {tool.icon}
                  </div>

                  {/* Text - All on one line, left aligned */}
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <h2 className="text-lg font-bold text-white leading-tight text-left m-0 whitespace-nowrap">{tool.name}</h2>
                    <p className="text-sm text-white/70 leading-tight text-left m-0">{tool.description}</p>
                  </div>
                </Link>
                <div className="tool-card-back bg-white/5 rounded-none px-6 py-4 flex items-center justify-center cursor-pointer">
                  <div className="text-center">
                    <div className="flex justify-center mb-4">
                      <LoadingSpinner size="lg" className="text-white" />
                    </div>
                    <p className="text-white/80 text-sm">Loading...</p>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {tools.length === 0 && (
        <div className="text-center py-20">
          <ArrowDownTrayIcon className="w-16 h-16 text-white/50 mx-auto mb-4" />
          <p className="text-white/70 text-lg">No tools available yet. Add your first tool!</p>
        </div>
      )}
    </div>
    </>
  )
}

